import { useMemo, useState } from 'react'

import { createPortal } from 'react-dom'
import { FaComment , FaHeart } from 'react-icons/fa'
import { FaUserGroup } from "react-icons/fa6";
import { MdOutlinePoll } from "react-icons/md";

import lockedPhoto from 'assets/images/locked.png'
import {
  EyeIcon,
  IconCameraOutline,
  IconGoalSmall,
  IconImageOutline,
  IconLockFill,
  IconPlayOutline,
  IconPostStatsChat,
  IconUnlocked,

} from 'assets/svg/sprite'
import PrefetchedBlobImage from 'components/Media/PrefetchedBlobImage'
import { formatDuration } from 'features/Vault/helpers/formatDuration'
import FullscreenVaultModal from 'features/Vault/modal/FullscreenVaultModal'
import { useUserProfile } from 'store'

import style from './_vault_card_grouped.module.scss'

export type ClientSortKey =
  | ''
  | 'most_recent'
  | 'conversion_rate'
  | 'views'
  | 'purchases'
  | 'impressions'
  | 'most_liked'
  | 'highest_tips'
  | 'most_comment'

type RestrictType = 'all_media' | 'photos' | 'videos'
type BadgeMode = 'completion'
type ViewMode = 'vault' | 'composer' | 'unshared' | 'shared_chat' | 'other'

interface VaultCardGroupedProps {
  media: any
  onCirlceClick: any
  selected: boolean
  selectedText: any
  grouped: boolean | string
  customClass?: any
  customCirlce?: any
  userMedia?: any
  checkingData?: any
  type?: any
  setPurchaseMsgId?: any
  setConfirmBuyModalOpen?: any
  setSelectedMessageBuyPrice?: any
  scrollToMessage?: any
  onSelectVariant?: (variant: any) => void
  isVariantSelected?: (key: string) => boolean
  getVariantIndex?: (key: string) => number | null
  badgeMode?: BadgeMode
  clientSortKey?: ClientSortKey
  showDuplicates?: boolean
  allTimeStats?: boolean
  restrictType?: RestrictType
  origin?: 'shared-media-modal-chat' | string
  vaultRoot?: Element | null
  vaultRootMargin?: string
  showSelection?: boolean
}

type Stats = {
  isVideo: boolean
  conversion_rate?: number
  views: number
  purchases: number
  impressions: number
  likes: number
  tips: number
  comments: number
}

const num = (v: any): number => (typeof v === 'number' && isFinite(v) ? v : Number(v) || 0)
const hasOwn = (o: any, k: string) => o != null && Object.prototype.hasOwnProperty.call(o, k)
const variantKeyOf = (item: any) => String(item?.__k ?? '')

export const VaultCardGrouped = ({
  media,
  onCirlceClick,
  selected,
  selectedText,
  grouped: groupedProp,
  customClass,
  customCirlce,
  checkingData,
  type,
  setPurchaseMsgId,
  setConfirmBuyModalOpen,
  setSelectedMessageBuyPrice,
  scrollToMessage,
  isVariantSelected,
  getVariantIndex,
  onSelectVariant,
  badgeMode,
  clientSortKey = '',
  showDuplicates = false,
  allTimeStats = false,
  restrictType,
  origin,
  showSelection = true,
  vaultRoot,
  vaultRootMargin,
}: VaultCardGroupedProps) => {
  const userData = useUserProfile()

  const [previewOpen, setPreviewOpen] = useState(false)
  const [selectedpostId, setSelectedpostId] = useState<any>()
  const [startingIndex, setStartingIndex] = useState(0)

  const mode = useMemo(() => {
    const path = window.location.pathname
    const isVault = path.startsWith('/vault')
    const isNewPostShared = path.includes('new/post/create')
    const isUnsharedModal = path === '/messages/new' || checkingData === 'checkingData'
    const isSharedChat = origin === 'shared-media-modal-chat'
    const isFan = userData?.role === 'fan'
    const grouped = typeof groupedProp === 'string' ? groupedProp === 'true' : !!groupedProp

    let view: ViewMode = 'other'
    if (isVault) view = 'vault'
    else if (isNewPostShared) view = 'composer'
    else if (isUnsharedModal) view = 'unshared'
    else if (isSharedChat) view = 'shared_chat'

    return {
      view,
      grouped,
      isVault,
      isNewPostShared,
      isUnsharedModal,
      isSharedChat,
      isFan,
    }
  }, [checkingData, groupedProp, origin, userData?.role])

  const PollIcon = MdOutlinePoll as unknown as (props: {
    className?: string
    size?: number | string
  }) => JSX.Element

  const UsersIcon = FaUserGroup as unknown as (props: {
    width?: number | string
    height?: number | string
    color?: number | string
  }) => JSX.Element

  const HeartIcon = FaHeart as unknown as (props: {
    size?: number | string
    color?: string
    className?: string
  }) => JSX.Element

  const CommentIcon = FaComment as unknown as (props: {
    size?: number | string
    color?: string
    className?: string
  }) => JSX.Element

  // locked placeholder ONLY for shared media chat fan view with missing URL.
  const safeSrc = (u?: string | null) => {
    if (mode.isSharedChat && mode.isFan && (!u || u === '')) return lockedPhoto
    return u ?? ''
  }

  const rt: RestrictType = restrictType ?? 'all_media'
  const baseKey = media?.id ?? media?.post?.id ?? media?.message?.message?.id ?? Math.random()

  const getPostPollGoalPhoto = (post: any) => {
    if (!post) return null

    if (post?.poll?.background_src || post?.poll?.background_src_thumb) {
      return {
        id: `poll-${post.poll.id ?? post.id}`,
        url: post.poll.background_src || post.poll.background_src_thumb,
        thumb_url: post.poll.background_src_thumb || post.poll.background_src,
        type: 'photo',
        __pollGoalMedia: true,
        __pollMedia: true,
      }
    }

if (post?.goal?.background_src || post?.goal?.background_src_thumb) {
    return {
        id: `goal-${post.goal.id ?? post.id}`,
        url: post.goal.background_src || post.goal.background_src_thumb,
        thumb_url: post.goal.background_src_thumb || post.goal.background_src,
        type: 'photo',
        __pollGoalMedia: true,
        __goalMedia: true,
    }
}

    return null
  }

  const variants = useMemo(() => {
    if (mode.grouped) return [{ ...media, __k: `${baseKey}-grouped` }]

    const out: any[] = []

    // Posts: photos
  if (rt !== 'videos') {
    const pPhotos = media?.post?.photos ?? []
    const pollGoalPhoto = getPostPollGoalPhoto(media?.post)

    const allPostPhotos = pollGoalPhoto && pPhotos.length === 0
      ? [pollGoalPhoto]
      : pPhotos

    for (const p of allPostPhotos) {
      out.push({
        ...media,
        __k: `${baseKey}-post-photo-${p.id ?? Math.random()}`,
        id: p.id,
        parent_id: media.id,
        post: {
          ...(media.post || {}),
          photos: [p],
          videos: [],
        },
      })
    }
  }

    // Posts: videos
    if (rt !== 'photos') {
      const pVideos = media?.post?.videos ?? []
      for (const v of pVideos) {
        out.push({
          ...media,
          __k: `${baseKey}-post-video-${v.id ?? Math.random()}`,
          id: v.id,
          parent_id: media.id,
          post: {
            ...(media.post || {}),
            photos: [],
            videos: [v],
          },
        })
      }
    }

    // Messages: photos
    if (rt !== 'videos') {
      const mPhotos = media?.message?.message?.photos ?? []
      for (const p of mPhotos) {
        out.push({
          ...media,
          __k: `${baseKey}-msg-photo-${p.id ?? Math.random()}`,
          id: p.id,
          parent_id: media.id,
          message: {
            ...(media.message || {}),
            message: {
              ...(media.message?.message || {}),
              photos: [p],
              videos: [],
            },
          },
        })
      }
    }

    // Messages: videos
    if (rt !== 'photos') {
      const mVideos = media?.message?.message?.videos ?? []
      for (const v of mVideos) {
        out.push({
          ...media,
          __k: `${baseKey}-msg-video-${v.id ?? Math.random()}`,
          id: v.id,
          parent_id: media.id,
          message: {
            ...(media.message || {}),
            message: {
              ...(media.message?.message || {}),
              photos: [],
              videos: [v],
            },
          },
        })
      }
    }

    // Story
    if (media?.story) {
      if (rt === 'all_media' && (media.story.image_src || media.story.video_src)) {
        out.push({ ...media, __k: `${baseKey}-story` })
      } else if (rt === 'photos' && media.story.image_src) {
        out.push({ ...media, __k: `${baseKey}-story-photo` })
      } else if (rt === 'videos' && media.story.video_src) {
        out.push({ ...media, __k: `${baseKey}-story-video` })
      }
    }

    return out.length ? out : [{ ...media, __k: `${baseKey}-fallback` }]
  }, [baseKey, media, mode.grouped, rt])

const mergedMedia = useMemo(() => {
  const combined: any[] = []

  if (media?.post) {
    const pollGoalPhoto = getPostPollGoalPhoto(media.post)

    combined.push(
      ...(media.post.photos ?? []).map((p: any) => ({
        ...p,
        type: 'photo',
        thumb_url: p.thumb_url || p.url,
      })),
      ...(pollGoalPhoto ? [pollGoalPhoto] : []),
      ...(media.post.videos ?? []).map((v: any) => ({
        ...v,
        type: 'video',
        thumb_url: v.thumb_url || v.active_thumb,
      }))
    )
  }

  if (media?.message?.message) {
    combined.push(
      ...(media.message.message.photos ?? []).map((p: any) => ({
        ...p,
        type: 'photo',
        thumb_url: p.thumb_url || p.url,
      })),
      ...(media.message.message.videos ?? []).map((v: any) => ({
        ...v,
        type: 'video',
        thumb_url: v.thumb_url || v.active_thumb,
      }))
    )
  }
if (media?.story) {
  if (media.story.image_src) {
    combined.push({
      ...media.story,
      id: media.story.id ?? media.id,
      type: 'photo',
      url: media.story.image_src,
      thumb_url: media.story.image_src,
    })
  }

  if (media.story.video_src) {
    combined.push({
      ...media.story,
      id: media.story.id ?? media.id,
      type: 'video',
      url: media.story.video_src,
      thumb_url: media.story.video_thumb_src || media.story.image_src,
    })
  }
}
  return combined
}, [media])

  const resolveVariantIndex = (variant: any): number => {
    const v0 = variant?.post?.videos?.[0] ?? variant?.message?.message?.videos?.[0]
    const p0 = variant?.post?.photos?.[0] ?? variant?.message?.message?.photos?.[0]
    const target = v0 ?? p0 ?? null
    if (!target) return 0

    const tid = target.id != null ? String(target.id) : null
    const tu = target.url ?? target.image_src ?? target.video_src ?? target.active_thumb ?? null

    const idx = mergedMedia.findIndex((it: any) => {
      const mid = it?.id != null ? String(it.id) : null
      const mu = it?.url ?? it?.image_src ?? it?.video_src ?? it?.active_thumb ?? null
      if (tid && mid) return tid === mid
      if (tu && mu) return tu === mu
      return false
    })

    return idx >= 0 ? idx : 0
  }

  // -------- stats / badges (UNCHANGED)
  const getStats = (m: any, grouped: boolean): Stats => {
    if (!grouped) {
      // UNGROUPED POST
      if (m?.post) {
        const v0 = m.post?.videos?.[0]
        const p0 = m.post?.photos?.[0]
        const item = v0 ?? p0
        const isVideo = !!v0

        const views = isVideo
          ? item?.views_unique
          : allTimeStats
            ? item?.impressions_all_time_unique
            : item?.impressions_unique

        const cr_present = item != null && hasOwn(item, 'conversion_rate')
        const cr_value = cr_present ? Number(item.conversion_rate) : undefined

        return {
          isVideo,
          conversion_rate: cr_value,
          views: num(views),
          purchases: num(item?.purchases),
          impressions: num(views),
          likes: 0,
          tips: 0,
          comments: 0,
        }
      }

      // UNGROUPED MESSAGE
      const v0 = m?.message?.message?.videos?.[0] ?? null
      const p0 = m?.message?.message?.photos?.[0] ?? null
      const item = v0 ?? p0

      const stats =
        m?.message?.message?.statistics ??
        m?.message?.statistics ??
        {}

      const views = item?.views_unique ?? item?.views ?? stats?.views

      const cr_present = item != null && hasOwn(item, 'conversion_rate')
      const cr_value = cr_present ? Number(item.conversion_rate) : undefined

      return {
        isVideo: !!v0,
        conversion_rate: cr_value,
        views: num(views),
        purchases: num(item?.purchases),
        impressions: 0,
        likes: 0,
        tips: 0,
        comments: 0,
      }
    }

    // GROUPED MESSAGE
    if (m?.message) {
      const isVideo =
        Array.isArray(m?.message?.message?.videos) &&
        m.message.message.videos.length > 0

      const stats =
        m?.message?.message?.statistics ??
        m?.message?.statistics ??
        {}

    return {
      isVideo,
      conversion_rate: num(stats?.conversion_rate),

      views: num(
        stats?.views_unique ??
          stats?.views ??
          m?.message?.message?.videos?.[0]?.views_unique ??
          m?.message?.message?.photos?.[0]?.views_unique
      ),

      purchases: num(
        stats?.purchases_count ??
          stats?.purchases
      ),

      impressions: 0,
      likes: 0,
      tips: 0,
      comments: 0,
    }
    }
    // STORY
    if (m?.story) {
      return {
        isVideo: !!m.story.video_src,
        conversion_rate: undefined,
        views: num(m.story.unique_views),
        purchases: 0,
        impressions: 0,
        likes: 0,
        tips: num(m.story.tips_sum),
        comments: 0,
      }
    }

    // GROUPED POST
    if (m?.post) {
      const groupRate =
        mode.grouped &&
        !showDuplicates &&
        typeof m?.post?.media_group_conversion_rate === 'number'
          ? m.post.media_group_conversion_rate
          : undefined

      const cr = groupRate ?? m?.post?.conversion_rate

      return {
        isVideo:
          Array.isArray(m?.post?.videos) &&
          m.post.videos.length > 0,

        conversion_rate: num(cr),
        purchases: num(m?.post?.purchases_count),

        views: num(
          m?.post?.views_unique ??
          m?.post?.unique_views ??
          m?.post?.views_raw
        ),

        impressions: num(
          m?.post?.impressions_unique ??
          m?.post?.unique_impressions
        ),
        likes: num(m?.post?.likes_count),
        tips: num(m?.post?.tips_sum),
        comments: num(m?.post?.comment_count),
      }
    }

    return {
      isVideo: false,
      conversion_rate: 0,
      views: 0,
      purchases: 0,
      impressions: 0,
      likes: 0,
      tips: 0,
      comments: 0,
    }
  }

  const renderMetricBadge = (m: any) => {
    if (!mode.isVault && !mode.isNewPostShared) return null

    const s = getStats(m, mode.grouped)
    const isMessage = !!m?.message
    const isPost = !!m?.post

    const isStory = !!m?.story

    if (isStory) {
      const views = num(m.story.unique_views)
      const tips = num(m.story.tips_sum)

      const metricContainerClass = mode.isVault
        ? style.media_bottom_tags_container_right
        : style.media_bottom_tags_container

      const chips: JSX.Element[] = []

      if (views > 0) {
        chips.push(
          <div className={style.media_bottom_tag} key="story-views">
            <div className={style.media_tags_background}></div>
            <div className={style.media_bottom_tag_text}>
              <EyeIcon width="16" height="12" color="#ffffff" />
              <span className={style.eyeIconCount}>{views}</span>
            </div>
          </div>
        )
      }

      if (tips > 0) {
        chips.push(
          <div className={style.media_bottom_tag} key="story-tips">
            <div className={style.media_tags_background}></div>
            <div className={style.media_bottom_tag_text}>
              <IconPostStatsChat />
              <span className={style.eyeIconCount}>${tips}</span>
            </div>
          </div>
        )
      }

      if (chips.length === 0) return null

      return <div className={metricContainerClass}>{chips}</div>
    }

    const shouldShowLikes =
      mode.grouped &&
      isPost &&
      clientSortKey === 'most_liked'

    const shouldShowTips =
      mode.grouped &&
      isPost &&
      clientSortKey === 'highest_tips'

    const shouldShowComments =
      mode.grouped &&
      isPost &&
      clientSortKey === 'most_comment'

    const shouldShowImpressions =
      isPost &&
      !shouldShowLikes &&
      !shouldShowTips &&
      !shouldShowComments &&
      (!clientSortKey || clientSortKey === 'impressions')

    const primaryCount = isMessage
      ? s.views
      : shouldShowLikes
        ? s.likes
        : shouldShowTips
          ? s.tips
          : shouldShowComments
            ? s.comments
            : shouldShowImpressions
              ? s.impressions
              : s.views
    const hasPrimary = Number(primaryCount) > 0

    const hasCR =
      s.conversion_rate !== null &&
      s.conversion_rate !== undefined &&
      Number.isFinite(Number(s.conversion_rate))

    const recipientCount = m?.message?.message?.sent_to

    const metricContainerClass = mode.isVault
      ? style.media_bottom_tags_container_right
      : style.media_bottom_tags_container

    const chips: JSX.Element[] = []



    if (clientSortKey === 'purchases' && Number(s.purchases) > 0) {
      chips.push(
        <div className={style.media_bottom_tag} key="purchases">
          <div className={style.media_tags_background}></div>
          <div className={style.media_bottom_tag_text}>
            <IconUnlocked />
            <span>{s.purchases}</span>
          </div>
        </div>
      )
    }

const isPriced = m?.post
  ? Number(m?.post?.price ?? 0) > 0
  : Number(m?.message?.message?.price ?? 0) > 0

const wantsCR = clientSortKey === 'conversion_rate' || !clientSortKey

const canShowCR =
  clientSortKey === 'conversion_rate'
    ? hasPrimary && hasCR
    : hasPrimary && hasCR && isPriced

const showCombinedMassMessageChip =
  !allTimeStats &&
  m?.mass_message_id &&
  hasPrimary &&
  wantsCR &&
  canShowCR

if (showCombinedMassMessageChip) {
  chips.push(
    <div className={style.media_bottom_tag} key="mass-message-combined">
      <div className={style.media_tags_background}></div>

      <div className={style.media_bottom_tag_text}>
        {shouldShowLikes ? (
          <HeartIcon size={12} color="#ffffff" />
        ) : shouldShowTips ? (
          <IconPostStatsChat />
        ) : shouldShowComments ? (
          <CommentIcon size={12} color="#ffffff" />
        ) : (
          <EyeIcon width="16" height="12" color="#ffffff" />
        )}

        <span>{shouldShowTips ? `$${primaryCount}` : primaryCount}</span>

        <span className={style.metricDivider}>|</span>

        <IconPostStatsChat />
        <span>{Math.round(Number(s.conversion_rate))}%</span>
      </div>
    </div>
  )
}

    if (!showCombinedMassMessageChip && hasPrimary) {
      chips.push(
        <div
          className={style.media_bottom_tag}
          key={
            shouldShowLikes
              ? 'likes'
              : shouldShowImpressions
                ? 'impressions'
                : 'views'
          }
        >
          <div className={style.media_tags_background}></div>
            <div className={style.media_bottom_tag_text}>
              {shouldShowLikes ? (
                <HeartIcon size={12} color="#ffffff" />
              ) : shouldShowTips ? (
                <IconPostStatsChat />
              ) : shouldShowComments ? (
                <CommentIcon size={12} color="#ffffff" />
              ) : (
                <EyeIcon width="16" height="12" color="#ffffff" />
              )}
              <span className={style.eyeIconCount}>
                {shouldShowTips ? `$${primaryCount}` : primaryCount}
              </span>
            </div>
        </div>
      )
    }
    if (!showCombinedMassMessageChip && !allTimeStats && m?.mass_message_id) {
      chips.push(
        <div className={style.media_bottom_tag} key="mass-message">
          <div className={style.media_tags_background}></div>

          <div className={style.media_bottom_tag_text}>
            <UsersIcon width="20" height="12" color="#ffffff" />
            <span>{recipientCount}</span>
          </div>
        </div>
      )
    }
    if (!showCombinedMassMessageChip && wantsCR && canShowCR) {
      chips.push(
        <div className={style.media_bottom_tag} key="cr">
          <div className={style.media_tags_background}></div>
          <div className={style.media_bottom_tag_text}>
            <IconPostStatsChat />
            <span>{Math.round(Number(s.conversion_rate))}%</span>
          </div>
        </div>
      )
    }

    if (chips.length === 0) return null
    return <div className={metricContainerClass}>{chips}</div>
  }



  
  // -------- badges (UNCHANGED)
  const getGroupedDisplayRate = (m: any): number | null => {
    if (badgeMode === 'completion') {
      const vids = m?.message?.message?.videos
      if (!Array.isArray(vids) || vids.length === 0) return null
      let max = 0
      for (const v of vids) {
        const cr = Number(v?.completion_rate ?? 0)
        if (cr > max) max = cr
      }
      return max
    }
    const stats =
      m?.message?.message?.statistics ??
      m?.message?.statistics ??
      {}

    const v = stats?.conversion_rate
    return typeof v === 'number' ? v : null
  }

  const getVariantDisplayRate = (m: any): number | null => {
    const v0 = m?.message?.message?.videos?.[0]
    const p0 = m?.message?.message?.photos?.[0]

    if (badgeMode === 'completion') {
      if (v0) return Number(v0?.completion_rate ?? 0)
      return null
    }

    const item = p0 ?? v0
    const v = item?.conversion_rate
    return typeof v === 'number' ? v : null
  }

  // -------- date chips (UNCHANGED)
  const renderDateTags = (m: any = media) => {
    const monthArray = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'July', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
    const isComposerPost = !!m?.post
    const price = isComposerPost ? Number(m?.post?.price ?? 0) : Number(m?.message?.message?.price ?? 0)
    const hasPoll = !!m?.post?.poll
    const hasGoal = !!m?.post?.goal

    const createdAt = m?.post?.created_at || m?.message?.message?.created_at || m?.created_at
    const date = createdAt ? new Date(createdAt) : null

    const recipientCount = Number(m?.message?.message?.sent_to ?? 0)

    const showRecipientCount =
      !allTimeStats &&
      !!m?.mass_message_id &&
      recipientCount > 0

    const showPriceDetails =
      mode.grouped &&
      (price > 0 || hasPoll || hasGoal)

    const combineRecipientAndPrice =
      showRecipientCount &&
      showPriceDetails

    if (!date) return null

if (mode.isVault) {
  return (
  <div className={style.top_left_anchor}>
  <div className={style.date_chip}>
    <div className={style.media_tags_background}></div>

    <div className={style.media_tags_date_text}>
      {`${monthArray[date.getMonth()]} ${date.getDate()}`}
    </div>
  </div>
{/* Combined recipient + price chip */}
{combineRecipientAndPrice && (
  <div className={style.combined_price_recipient_chip}>
    <div className={style.media_tags_background}></div>

    <div className={style.combined_price_recipient_row}>
      <UsersIcon width="20" height="12" color="#ffffff" />
      <span>{recipientCount}</span>
    </div>

    <div className={style.combined_price_recipient_divider}></div>

    <div className={style.combined_price_recipient_row}>
      {price > 0 && (
        <>
          <IconLockFill />
          <span>${price}</span>
        </>
      )}

      {hasPoll && <PollIcon size={14} />}

      {hasGoal && <IconGoalSmall />}
    </div>
  </div>
)}

{/* Recipient only: keep original chip appearance */}
{showRecipientCount && !showPriceDetails && (
  <div className={style.media_bottom_tag}>
    <div className={style.media_tags_background}></div>

    <div className={style.media_bottom_tag_text}>
      <UsersIcon width="20" height="12" color="#ffffff" />
      <span>{recipientCount}</span>
    </div>
  </div>
)}

{/* Price only: keep original chip appearance */}
{showPriceDetails && !showRecipientCount && (
  <div className={style.price_chip_top_left}>
    <div className={style.media_tags_background}></div>

    <div className={style.media_bottom_tag_text}>
      {price > 0 && (
        <>
          <IconLockFill />
          <span>${price}</span>
        </>
      )}

      {hasPoll && <PollIcon size={14} />}

      {hasGoal && <IconGoalSmall />}
    </div>
  </div>
)}
    </div>
  )
}
    // composer/unshared layout top-left + optional price (your behavior)
    if (mode.isUnsharedModal || mode.isNewPostShared) {

      return (
        <div className={style.top_left_anchor}>
          <div className={style.date_chip}>
            <div className={style.media_tags_background}></div>
            <div className={style.media_tags_date_text}>{`${monthArray[date.getMonth()]} ${date.getDate()}`}</div>
          </div>

          {price > 0 && (
            <div className={style.price_chip_top_left}>
              <div className={style.media_tags_background}></div>
              <div className={style.media_bottom_tag_text}>
                <IconLockFill />
                <span>${price}</span>
              </div>
            </div>
          )}
        </div>
      )
    }

    // default behavior (kept as-is)
    const shouldShowMediaTag =
      window.location.pathname === '/vault' ||
      checkingData === 'checkingData' ||
      (userData?.role === 'fan' &&
        !(Array.isArray(m?.message?.purchased) && m.message.purchased.length > 0) &&
        m.message?.message?.price > 0) ||
      (userData?.role === 'model' &&
        ((Array.isArray(m?.message?.purchased) && m.message.purchased.length > 0 && m.message?.message?.price > 0) ||
          m.message?.message?.price > 0))

    if (!shouldShowMediaTag) return null

    return (
      <div className={style.media_tags_date_container}>
        <div className={style.media_tags_background}></div>
        <div className={style.media_tags_date_text}>
          {window.location.pathname === '/vault' || checkingData === 'checkingData' ? (
            `${monthArray[date.getMonth()]} ${date.getDate()}`
          ) : userData?.role === 'fan' ? (
            Array.isArray(m?.message?.purchased) && m.message.purchased.length > 0 && m.message?.message?.price > 0 ? null : (
              m.message?.message?.price > 0 && (
                <div className={style.media_bottom_tag_text}>
                  <IconLockFill />
                  <span>${m.message.message.price}</span>
                </div>
              )
            )
          ) : userData?.role === 'model' ? (
            Array.isArray(m?.message?.purchased) && m.message.purchased.length > 0 && m.message?.message?.price > 0 ? (
              'Sold'
            ) : (
              m.message?.message?.price > 0 && (
                <div className={style.media_bottom_tag_text}>
                  <IconLockFill />
                  <span>${m.message.message.price}</span>
                </div>
              )
            )
          ) : null}
        </div>
      </div>
    )
  }

  // -------- tiny internal UI pieces (UNCHANGED)
  const GroupCountsChips = ({
    videos,
    photos,
    variant,
  }: {
    videos: number
    photos: number
    variant: 'default' | 'newPost'
  }) => {
    const showVideoChip =
      (videos > 0 && photos > 0) || (videos > 1 && photos === 0)
    const showPhotoChip =
      (photos > 0 && videos > 0) || (videos === 0 && photos > 1)

    const containerClass =
      variant === 'newPost'
        ? style.media_bottom_tags_container_new_post
        : style.media_bottom_tags_container

    return (
      <div className={containerClass}>
        {showVideoChip && (
          <div className={style.media_bottom_tag}>
            <div className={style.media_tags_background}></div>
            <div className={style.media_bottom_tag_text}>
              <IconCameraOutline width="16" height="12" color="#ffffff" />
              <span>{videos}</span>
            </div>
          </div>
        )}

        {showPhotoChip && (
          <div className={style.media_bottom_tag}>
            <div className={style.media_tags_background}></div>
            <div className={style.media_bottom_tag_text}>
              <IconImageOutline width="12" height="12" color="#ffffff" />
              <span>{photos}</span>
            </div>
          </div>
        )}
      </div>
    )
  }

const AnalyticsChips = ({
  views,
  rate,
  showRate,
}: {
  views: number
  rate: number | null
  showRate: boolean
}) => {
  return (
    <>
      {views > 0 && (
        <div className={style.analytics_media_bottom_tag}>
          <div className={style.analytics_media_bottom_tag_text}>
            <EyeIcon width="16" height="12" color="#ffffff" />
            <span>{views}</span>
          </div>
        </div>
      )}

      {showRate && rate !== null && (
        <div className={style.analytics_media_bottom_tag}>
          <div className={style.analytics_media_bottom_tag_text}>
            <IconPostStatsChat />
            <span>{Math.round(Number(rate))}%</span>
          </div>
        </div>
      )}
    </>
  )
}

  const renderTags = (m: any = media) => {
    const viewsStat = Number(m?.message?.message?.statistics?.views ?? 0)

    // POSTS
    if (m?.post) {
      if (mode.grouped) {
        const videos = m.post?.videos?.length ?? 0
        const photos = m.post?.photos?.length ?? 0
        if (videos > 0 || photos > 0) {
          if (mode.isNewPostShared && (type === 'feed' || type === 'premium')) {
            return (
              <div
                className={style.grouped_post_counts_anchor}
                data-debug="grouped-post-counts-new-post"
              >
                <GroupCountsChips videos={videos} photos={photos} variant="default" />
              </div>
            )
          }

          return <GroupCountsChips videos={videos} photos={photos} variant="default" />
        }
      } else {
        // ungrouped post: duration tag for video (now only if videoDuration is known)
        const v0 = m?.post?.videos?.[0]
        const label = formatDuration(v0?.duration)

        if (v0 && label) {
          return (
            <div className={style.media_bottom_tags_container}>
              <div className={style.media_bottom_tag}>
                <div className={style.media_tags_background}></div>
                <div className={style.media_bottom_tag_text}>
                  <span>{label}</span>
                </div>
              </div>
            </div>
          )
        }
      }
    }

    // STORY (ungrouped duration) — only if we somehow have a duration
    if (m?.story && !mode.grouped && m.story.video_src) {
      const label = formatDuration(m.story.duration)

      if (!label) return null

      return (
        <div className={style.media_bottom_tags_container}>
          <div className={style.media_bottom_tag}>
            <div className={style.media_tags_background}></div>
            <div className={style.media_bottom_tag_text}>
              <span>{label}</span>
            </div>
          </div>
        </div>
      )
    }

    // MESSAGES
    if (m?.message?.message) {
      const videos = m.message.message?.videos?.length ?? 0
      const photos = m.message.message?.photos?.length ?? 0

      if (mode.grouped) {
        if (videos > 0 || photos > 0) {
          // composer messages layout
          if (mode.isNewPostShared && type === 'messages') {
            return (
              <div className={style.new_Post_Shared_Media}>
                <GroupCountsChips videos={videos} photos={photos} variant="newPost" />
              </div>
            )
          }

          // vault or fan: show count chips (original)
          if (mode.isVault || userData?.role === 'fan') {
            return <GroupCountsChips videos={videos} photos={photos} variant="default" />
          }

          // unshared modal: analytics row
          if (mode.isUnsharedModal) {
            const rate = getGroupedDisplayRate(m)
            const showRate =
              badgeMode === 'completion'
                ? rate !== null
                : (viewsStat > 0 && rate !== null)

            return (
              <div className={style.analytics_media_bottom_tags_container}>
                {videos > 1 && (
                  <div className={style.analytics_media_bottom_tag}>
                    <div className={style.analytics_media_bottom_tag_text}>
                      <IconCameraOutline width="16" height="12" color="#ffffff" />
                      <span>{videos}</span>
                    </div>
                  </div>
                )}

                {photos > 1 && (
                  <div className={style.analytics_media_bottom_tag}>
                    <div className={style.analytics_media_bottom_tag_text}>
                      <IconImageOutline width="12" height="12" color="#ffffff" />
                      <span>{photos}</span>
                    </div>
                  </div>
                )}

                <AnalyticsChips views={viewsStat} rate={rate} showRate={showRate} />
              </div>
            )
          }

          // model default: count chips (original)
          if (userData?.role === 'model') {
            return <GroupCountsChips videos={videos} photos={photos} variant="default" />
          }
        }
      } else {
        // UNGROUPED message: one media per variant
        const v0 = m.message?.message?.videos?.[0]
        const p0 = m.message?.message?.photos?.[0]

        if (mode.isUnsharedModal) {
          if (v0) {
            const vViews = Number(v0?.views ?? 0)
            const vRate = getVariantDisplayRate(m)
            const showRate =
              badgeMode === 'completion'
                ? vRate !== null
                : (vViews > 0 && vRate !== null)

            return <AnalyticsChips views={vViews} rate={vRate} showRate={showRate} />
          }

          if (p0) {
            const pViews = Number(p0?.views ?? 0)
            const pRate = getVariantDisplayRate(m)
            const showRate =
              badgeMode === 'completion'
                ? false
                : (pViews > 0 && pRate !== null)

            return <AnalyticsChips views={pViews} rate={pRate} showRate={showRate} />
          }

          return null
        }

        // outside unshared modal: duration chip for a single message video
        if (v0) {
          const label = formatDuration(v0.duration)

          if (!label) return null

          return (
            <div className={style.media_bottom_tags_container}>
              <div className={style.media_bottom_tag}>
                <div className={style.media_tags_background}></div>
                <div className={style.media_bottom_tag_text}>
                  <IconCameraOutline width="16" height="12" color="#ffffff" />
                  {window.location.pathname === '/messages/new' ? '' : <span>{label}</span>}
                </div>
              </div>
            </div>
          )
        }

        if (p0) return null
      }
    }

    return null
  }

  //  Thumb renderer: videos now behave like photos (image thumb only)
  // - Post video => active_thumb
  // - Message video => active_thumb
  // - Story video => video_thumb_src
  // Uses PrefetchedBlobImage so it consumes prefetched blobs when available.
  const renderCardMedia = (m: any = media) => {
    // POSTS
    if (m?.post) {
      const pollGoalPhoto = getPostPollGoalPhoto(m.post)

      const orderedMedia = [
        ...(m.post?.photos ?? []),
        ...(pollGoalPhoto && !(m.post?.photos?.length ?? 0) ? [pollGoalPhoto] : []),
        ...(m.post?.videos ?? []),
      ].sort(
        (a, b) =>
          Number(a?.order ?? Number.MAX_SAFE_INTEGER) -
          Number(b?.order ?? Number.MAX_SAFE_INTEGER)
      )

      const primary = orderedMedia[0]

      const v = primary?.type === 'video' ? primary : null
      const p = primary?.type === 'photo' ? primary : null

      if (v) {
        const thumb = safeSrc(v?.active_thumb)

        return (
          <>
            <PrefetchedBlobImage
              url={thumb}
              prefetchUrls={[v?.url || v?.src]}
              root={vaultRoot ?? null}
              rootMargin={vaultRootMargin ?? '232px 0px 232px 0px'}
              className={style.prefetch_fill}
              imgClassName={style.media_image}
              alt=""
            />
            {/* keep play icon overlay, but DO NOT play inline */}
            <div className={style.video_play}>
              <IconPlayOutline color="#ffffff" />
            </div>
          </>
        )
      }

if (p?.thumb_url) {
  return (
    <PrefetchedBlobImage
      url={safeSrc(p.thumb_url)}
      prefetchUrls={[p?.url].filter(Boolean)}
      root={vaultRoot ?? null}
      rootMargin={vaultRootMargin ?? '232px 0px 232px 0px'}
      className={style.prefetch_fill}
      imgClassName={style.media_image}
      alt=""
    />
  )
}
    }

    // STORIES
    if (m?.story) {
      if (m.story.image_src_thumb) {
        return (
          <PrefetchedBlobImage
            url={safeSrc(m.story.image_src_thumb)}
            prefetchUrls={[m.story.image_src].filter(Boolean)}
            root={vaultRoot ?? null}
            rootMargin={vaultRootMargin ?? '232px 0px 232px 0px'}
            className={style.prefetch_fill}
            imgClassName={style.media_image}
            alt=""
          />
        )
      }

      if (m.story.video_src && m.story.video_thumb_src) {
        return (
          <>
            <PrefetchedBlobImage
              url={safeSrc(m.story.video_thumb_src)}
              prefetchUrls={[m.story.video_src].filter(Boolean)}
              root={vaultRoot ?? null}
              rootMargin={vaultRootMargin ?? '232px 0px 232px 0px'}
              className={style.prefetch_fill}
              imgClassName={style.media_image}
              alt=""
            />
            <div className={style.video_play}>
              <IconPlayOutline color="#ffffff" />
            </div>
          </>
        )
      }
    }

    // MESSAGES
    if (m?.message?.message) {
      const msg = m.message.message

      const v = msg?.videos?.[0]
      const p = msg?.photos?.[0]

      // fallback to preview thumbs when locked (shared_chat fan)
      const vp = msg?.videos_preview?.[0]
      const pp = msg?.photos_preview?.[0]

      // If you have a direct boolean, prefer it. Otherwise infer from arrays.
      const isPurchased =
        typeof msg?.is_purchased === 'boolean'
          ? msg.is_purchased
          : (Array.isArray(m?.message?.purchased) ? m.message.purchased.length > 0 : false)

      const canUsePreview = mode.isSharedChat && mode.isFan && !isPurchased

      // ----- VIDEO THUMB
      const videoThumb =
        v?.active_thumb ||
        (canUsePreview ? (vp?.preview?.src || vp?.active_thumb || '') : '')

      if (videoThumb) {
        return (
          <>
            <PrefetchedBlobImage
              url={safeSrc(videoThumb)}
              prefetchUrls={[v?.url || v?.src]}
              root={vaultRoot ?? null}
              rootMargin={vaultRootMargin ?? '232px 0px 232px 0px'}
              className={style.prefetch_fill}
              imgClassName={style.media_image}
              alt=""
            />
            <div className={style.video_play}>
              <IconPlayOutline color="#ffffff" />
            </div>
          </>
        )
      }

      // ----- PHOTO THUMB
      const photoThumb =
        p?.thumb_url ||
        (canUsePreview ? (pp?.preview?.src || '') : '')

      if (photoThumb) {
        return (
          <PrefetchedBlobImage
            url={safeSrc(photoThumb)}
            prefetchUrls={[p?.url].filter(Boolean)}
            root={vaultRoot ?? null}
            rootMargin={vaultRootMargin ?? '232px 0px 232px 0px'}
            className={style.prefetch_fill}
            imgClassName={style.media_image}
            alt=""
          />
        )
      }
    }


    return null
  }

  // composer-only header stub (kept)
  const renderCardHeader = (m: any = media) => {
    if (m?.post?.deleted_at === null) {
      return (
        <div className={style.card_header}>
          <div className={style.media_tags_date_text}>{/* online */}</div>
        </div>
      )
    }

    return (
      <div className={style.card_header}>
        <div className={style.media_tags_date_text}>{/* offline */}</div>
      </div>
    )
  }

  const modalRoot = typeof document !== 'undefined' ? document.body : null

  return (
    <>
      {variants.map((m, idx) => {
        const k = variantKeyOf(m) || String(idx)

        const variantSelected = mode.grouped ? !!selected : !!isVariantSelected?.(k)

        const displayIndex = mode.grouped
          ? (selected ? (Number.isFinite(Number(selectedText)) ? Number(selectedText) : selectedText) : '')
          : (getVariantIndex?.(k) ?? '')

        return (
          <div key={k} className={`${customClass ?? ''} ${style.card_wrapper}`}>
            <div
              onClick={() => {
                const ix = mode.grouped ? 0 : resolveVariantIndex(m)

                setStartingIndex(ix)
                setPreviewOpen(true)
                setSelectedpostId(
                  m?.message?.message?.id ?? media?.message?.message?.id
                )
              }}
              className={`${style.card_container} ${variantSelected ? style.card_container_selected : ''}`}
            >
              {showSelection && (
                <div
                  onClick={e => {
                    e.stopPropagation()
                    if (mode.grouped) onCirlceClick?.(media)
                    else onSelectVariant?.(m)
                  }}
                  className={`${customCirlce ?? ''} ${style.cirlce} ${variantSelected ? style.cirlce_selected : ''}`}
                >
                  {displayIndex || ''}
                </div>
              )}
              {mode.isNewPostShared && type !== 'messages' && renderCardHeader(m)}

              {renderCardMedia(m)}
              {renderDateTags(m)}

              {mode.isVault ? (
                <div className={style.bottom_row}>
                  {/* keep your original special-case: don't show renderTags in vault when ungrouped */}
                  {!(mode.isVault && !mode.grouped) && renderTags(m)}
                  {renderMetricBadge(m)}
                </div>
              ) : (
                <>
                  {renderTags(m)}
                  {renderMetricBadge(m)}
                </>
              )}
            </div>
          </div>
        )
      })}

      {previewOpen && modalRoot
        ? createPortal(
            <FullscreenVaultModal
              media={mergedMedia || []}
              closeFn={() => setPreviewOpen(false)}
              scrollToMessage={scrollToMessage}
              startingIndex={startingIndex}
              selectedpostId={selectedpostId}
              post={media?.post}
              message={media?.message}
            />,
            modalRoot
          )
        : null}
    </>
  )
}
